import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Users, Copy, Check, Coins, Hotel, ArrowRight, Share2, Loader2, TrendingUp, Clock, Shield, Star } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { BACKEND_URL } from '@/lib/backendUrl';

const FOUNDING_REFERRER_LIMIT = 50;

function StatCard({ label, value, icon: Icon, accent, sub }) {
  return (
    <div className="bg-card/60 border border-border rounded-xl p-4 space-y-1">
      <div className="flex items-center gap-2 text-muted-foreground text-xs">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <p className={cn("text-2xl font-bold mono", accent)}>{value}</p>
      {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
    </div>
  );
}

export default function ReferralTab({ hotelId }) {
  const [code, setCode] = useState(null);
  const [stats, setStats] = useState(null);
  const [sbts, setSbts] = useState(null);
  const [globalSbtStats, setGlobalSbtStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [codeRes, statsRes, sbtsRes, globalRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/referrals/${hotelId}/code`),
        fetch(`${BACKEND_URL}/api/referrals/${hotelId}/stats`),
        fetch(`${BACKEND_URL}/api/referrals/${hotelId}/sbts`),
        fetch(`${BACKEND_URL}/api/referrals/sbt-stats/global`),
      ]);
      setCode(await codeRes.json());
      setStats(await statsRes.json());
      setSbts(await sbtsRes.json());
      setGlobalSbtStats(await globalRes.json());
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [hotelId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCopy = () => {
    if (code?.referral_code) {
      navigator.clipboard.writeText(code.referral_code);
      setCopied(true);
      toast.success('Referral code copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleShareLink = () => {
    const link = `${window.location.origin}/?ref=${code?.referral_code}`;
    navigator.clipboard.writeText(link);
    toast.success('Referral link copied!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="referral-tab">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold heading-font flex items-center gap-2">
          <Users className="w-5 h-5 text-primary" />
          Hotel Referral Program
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Refer other hotels to getbitsy.ai — getbitsy gives you its entire 1% HERMES revenue from their bookings, for life
        </p>
      </div>

      {/* Referral Code Card */}
      <Card className="border-2 border-primary/20" data-testid="referral-code-card">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex-1">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Your Referral Code</p>
              <div className="flex items-center gap-2">
                <code className="text-2xl font-bold mono text-primary tracking-wider" data-testid="referral-code-display">
                  {code?.referral_code || '—'}
                </code>
                <Button size="sm" variant="ghost" onClick={handleCopy} data-testid="copy-code-btn">
                  {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={handleCopy} data-testid="copy-code-btn-secondary">
                <Copy className="w-4 h-4 mr-1" /> Copy Code
              </Button>
              <Button size="sm" onClick={handleShareLink} data-testid="share-link-btn">
                <Share2 className="w-4 h-4 mr-1" /> Copy Link
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            Share this code with other hotel owners. When they sign up and start receiving bookings, getbitsy gives you its entire 1% HERMES revenue from their bookings — for life, as long as both hotels are active.
          </p>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          label="Hotels Referred"
          value={stats?.total_referrals || 0}
          icon={Hotel}
        />
        <StatCard
          label="HERMES Collected"
          value={`${(stats?.total_hermes_collected || 0).toLocaleString()} H`}
          icon={Coins}
          accent="text-primary"
        />
        <StatCard
          label="Referred Revenue"
          value={`$${(stats?.total_referred_revenue || 0).toLocaleString()}`}
          icon={TrendingUp}
          accent="text-emerald-500"
          sub="Total bookings at referred hotels"
        />
        <StatCard
          label="Referral Rate"
          value="1%"
          icon={Users}
          sub="getbitsy's full HERMES share goes to you"
        />
      </div>

      {/* How It Works */}
      <Card data-testid="how-it-works-card">
        <CardContent className="p-5">
          <h3 className="font-semibold text-sm heading-font mb-4">How It Works</h3>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-6">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">1</div>
              <div>
                <p className="text-sm font-medium">Share Your Code</p>
                <p className="text-[10px] text-muted-foreground">Give your referral code to another hotel owner</p>
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-muted-foreground hidden sm:block" />
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">2</div>
              <div>
                <p className="text-sm font-medium">They Sign Up</p>
                <p className="text-[10px] text-muted-foreground">Hotel joins getbitsy using your code</p>
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-muted-foreground hidden sm:block" />
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">3</div>
              <div>
                <p className="text-sm font-medium">Collect HERMES</p>
                <p className="text-[10px] text-muted-foreground">1% of their booking revenue — getbitsy's full HERMES share goes to you</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Referred By */}
      {stats?.referred_by && (
        <Card className="border-dashed">
          <CardContent className="p-4 flex items-center gap-3">
            <Users className="w-5 h-5 text-muted-foreground" />
            <div>
              <p className="text-sm">You were referred by <span className="font-semibold">{stats.referred_by.hotel_name}</span></p>
              <p className="text-[10px] text-muted-foreground">Since {new Date(stats.referred_by.since).toLocaleDateString()}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Referred Hotels List */}
      {stats?.referred_hotels?.length > 0 && (
        <Card data-testid="referred-hotels-card">
          <CardContent className="p-5">
            <h3 className="font-semibold text-sm heading-font mb-3 flex items-center gap-2">
              <Hotel className="w-4 h-4 text-primary" />
              Hotels You Referred ({stats.referred_hotels.length})
            </h3>
            <div className="space-y-2">
              {stats.referred_hotels.map((h) => (
                <div key={h.hotel_id} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-muted/30">
                  <div>
                    <p className="text-sm font-medium">{h.hotel_name || h.hotel_id}</p>
                    <p className="text-[10px] text-muted-foreground">Since {new Date(h.since).toLocaleDateString()}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold mono text-primary">{h.hermes_distributed.toLocaleString()} H</p>
                    <Badge variant="outline" className="text-[9px]">{h.status}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Referral SBTs */}
      {sbts?.sbts?.length > 0 && (
        <Card data-testid="referral-sbts-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-sm heading-font flex items-center gap-2">
                <Shield className="w-4 h-4 text-primary" />
                Referral SBTs ({sbts.total_sbts})
              </h3>
              <div className="flex gap-2 text-[10px]">
                {sbts.founding_count > 0 && (
                  <Badge className="bg-[#F7931A]/10 text-[#F7931A] border-[#F7931A]/20">
                    <Star className="w-2.5 h-2.5 mr-1" /> {sbts.founding_count} Founding
                  </Badge>
                )}
                {sbts.standard_count > 0 && (
                  <Badge variant="outline" className="text-[10px]">{sbts.standard_count} Standard</Badge>
                )}
              </div>
            </div>
            <div className="space-y-3">
              {sbts.sbts.map((sbt) => (
                <div key={sbt.token_id} className={cn(
                  "border rounded-xl p-4 transition-colors",
                  sbt.tier === 'founding' ? 'border-[#F7931A]/30 bg-[#F7931A]/[0.02]' : 'border-border'
                )}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        "w-10 h-10 rounded-xl flex items-center justify-center",
                        sbt.tier === 'founding' ? 'bg-[#F7931A]/10' : 'bg-primary/10'
                      )}>
                        <Shield className={cn("w-5 h-5", sbt.tier === 'founding' ? 'text-[#F7931A]' : 'text-primary')} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-sm">SBT #{sbt.token_id}</p>
                          <Badge className={cn(
                            "text-[9px]",
                            sbt.tier === 'founding' ? 'bg-[#F7931A]/10 text-[#F7931A]' : 'bg-primary/10 text-primary'
                          )}>
                            {sbt.tier_name}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Referred: <span className="font-medium text-foreground">{sbt.referred_hotel_name}</span>
                        </p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">
                          Minted {new Date(sbt.minted_at).toLocaleDateString()} | {sbt.claim_pct}% lifetime claim
                          {sbt.on_chain && (
                            <> | <a href={sbt.explorer_url || `https://sepolia.basescan.org/tx/${sbt.tx_hash}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">On-chain</a></>
                          )}
                          {!sbt.on_chain && <> | <span className="text-muted-foreground">Off-chain</span></>}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold mono text-primary">{(sbt.total_hermes_claimed || 0).toLocaleString()} H</p>
                      <p className="text-[10px] text-muted-foreground">HERMES claimed</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Global SBT Stats */}
      {globalSbtStats && (
        <Card className="border-dashed" data-testid="global-sbt-stats-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2 text-sm">
                <Shield className="w-4 h-4 text-[#F7931A]" />
                <span className="font-medium">Founding Referrer Program</span>
              </div>
              <div className="flex gap-4 text-xs text-muted-foreground">
                <span>{globalSbtStats.founding_referrers} / {FOUNDING_REFERRER_LIMIT} founding slots used</span>
                <span className={globalSbtStats.founding_slots_remaining > 0 ? 'text-emerald-500 font-medium' : 'text-red-500'}>
                  {globalSbtStats.founding_slots_remaining > 0
                    ? `${globalSbtStats.founding_slots_remaining} founding slots remaining`
                    : 'Founding slots filled'
                  }
                </span>
              </div>
            </div>
            <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-[#F7931A] rounded-full transition-all"
                style={{ width: `${Math.min((globalSbtStats.founding_referrers / FOUNDING_REFERRER_LIMIT) * 100, 100)}%` }}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Distributions */}
      {stats?.recent_distributions?.length > 0 && (
        <Card data-testid="distributions-card">
          <CardContent className="p-5">
            <h3 className="font-semibold text-sm heading-font mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4 text-primary" />
              Recent HERMES Distributions
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="distributions-table">
                <thead>
                  <tr className="border-b border-border text-left text-muted-foreground">
                    <th className="pb-2 pr-3">Date</th>
                    <th className="pb-2 pr-3">Referred Hotel</th>
                    <th className="pb-2 pr-3 text-right">Booking Revenue</th>
                    <th className="pb-2 text-right">HERMES Distributed</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_distributions.map((d, i) => (
                    <tr key={i} className="border-b border-border/50 hover:bg-muted/30">
                      <td className="py-2 pr-3 mono text-muted-foreground">{d.timestamp?.slice(0, 10)}</td>
                      <td className="py-2 pr-3">{d.referred_hotel_id}</td>
                      <td className="py-2 pr-3 text-right mono">${d.booking_revenue.toLocaleString()}</td>
                      <td className="py-2 text-right mono font-semibold text-primary">{d.hermes_distributed.toLocaleString()} H</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {(!stats?.referred_hotels?.length) && (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center">
            <Users className="w-10 h-10 mx-auto mb-3 text-muted-foreground/30" />
            <p className="font-medium mb-1">No referrals yet</p>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              Share your referral code with other hotel owners. When they join getbitsy.ai and start receiving bookings, getbitsy gives you its entire 1% HERMES revenue from their bookings — for life, as long as both hotels are active.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
